<template>
    <b-modal :visible="value" @change="onVisibleChange" :title="title" size="lg" hide-footer>
        <div v-if="loading" class="text-center p-3 d-flex align-items-center justify-content-center"
            style="gap: 6px;">
            <b-spinner small /> Loading ledger...
        </div>

        <template v-else>
            <b-table-simple small responsive class="mb-2">
                <thead>
                    <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Reason</th>
                        <th>Notes</th>
                        <th class="text-right">Amount</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="!entries.length">
                        <td colspan="5" class="text-center text-muted">
                            No transactions posted in this statement's period.
                        </td>
                    </tr>
                    <tr v-for="entry in entries" :key="entry.id">
                        <td>{{ formatDate(entry.created) }}</td>
                        <td>{{ transactionTypeLabel(entry.transactionType) }}</td>
                        <td>{{ entry.reasonCode }}</td>
                        <td>{{ (entry.notes || "").trim() || "-" }}</td>
                        <td class="text-right">{{ currency(entry.amount) }}</td>
                    </tr>
                </tbody>
                <tfoot v-if="entries.length">
                    <tr>
                        <th colspan="4" class="text-right">Period Total</th>
                        <th class="text-right">{{ currency(periodTotal) }}</th>
                    </tr>
                </tfoot>
            </b-table-simple>

            <!-- This reads the account's whole ledger; the PDF's line detail goes through the
                 addressee visibility filter, so on a multi-patient account the two lists can differ
                 line for line even though both totals come from the same unfiltered period sums. -->
            <small class="text-muted">
                Shows the account's ledger narrowed to this statement's period. The Period Total
                reconciles with the statement, but on a multi-patient account the PDF prints only the
                lines its addressee is permitted to see.
            </small>
        </template>

        <div class="text-right mt-2">
            <b-button variant="secondary" @click="close">Close</b-button>
        </div>
    </b-modal>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import axios from "axios";
import moment from "moment";
import { ARLedgerEntry, ARStatementSummary, arTransactionTypeLabel } from "@/models/AR";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import TextUtils from "@/util/TextUtils";

@Component({
    name: "ARStatementLedgerModal",
})
export default class ARStatementLedgerModal extends Vue {
    @Prop({ type: Boolean, default: false }) value!: boolean;
    @Prop({ default: null }) statement!: ARStatementSummary | null;

    private loading = false;
    private entries: ARLedgerEntry[] = [];

    get title(): string {
        if (!this.statement) return "Statement Ledger";
        return `Statement Ledger — ${this.formatPeriodDate(this.statement.periodStart)} to ${this.formatPeriodDate(this.statement.periodEnd)}`;
    }

    get periodTotal(): number {
        return this.entries.reduce((sum, entry) => sum + (entry.amount ?? 0), 0);
    }

    @Watch("value")
    async onShow(visible: boolean): Promise<void> {
        if (!visible) return;
        this.entries = [];
        await this.loadLedger();
    }

    private async loadLedger(): Promise<void> {
        const accountId = this.statement?.arAccountId;
        if (!accountId) return;
        try {
            this.loading = true;
            const response = await axios.get<ARLedgerEntry[]>(`/AR/accounts/${accountId}/ledger`);
            this.entries = this.filterToPeriod(response.data ?? []);
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
            this.entries = [];
        } finally {
            this.loading = false;
        }
    }

    // Deliberately the same comparison the statement renderer uses to pick its period lines
    // (inclusive at both ends, compared as timestamps). Widening it here — to end-of-day, say —
    // would let the drill-through list charges the statement itself never counted, so the two
    // would stop reconciling. If the renderer's period logic changes, this has to change with it.
    filterToPeriod(entries: ARLedgerEntry[]): ARLedgerEntry[] {
        const start = this.statement?.periodStart ? moment.utc(this.statement.periodStart) : null;
        const end = this.statement?.periodEnd ? moment.utc(this.statement.periodEnd) : null;
        return entries.filter(entry => {
            if (!entry.created) return false;
            const created = moment.utc(entry.created);
            if (start && created.isBefore(start)) return false;
            if (end && created.isAfter(end)) return false;
            return true;
        });
    }

    transactionTypeLabel(type?: number): string {
        return arTransactionTypeLabel(type);
    }

    currency(value?: number | null): string {
        return TextUtils.currencyFormatter(value ?? 0);
    }

    /**
     * Ledger rows carry a real posting instant recorded in UTC, so they are shown in the viewer's
     * zone. The statement's period bounds are date-only and must not be converted — see
     * formatPeriodDate.
     */
    formatDate(date?: string | null): string {
        return date ? moment.utc(date).local().format("L") : "";
    }

    /** Period bounds are date-only; converting them rolls midnight back a day west of UTC. */
    formatPeriodDate(date?: string | null): string {
        return date ? moment(date).format("L") : "";
    }

    onVisibleChange(visible: boolean): void {
        this.$emit("input", visible);
    }

    close(): void {
        this.$emit("input", false);
    }
}
</script>

<style scoped></style>
